import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {memo, useCallback, useRef} from 'react';
import Measure from 'react-measure';
import {shallowEqual} from 'react-redux';

import {PANEL_BANK_PADDING} from '../../util/panelbankTypes';
import {usePanelBankWidthContext} from './contextProviders/PanelBankWidthContext';
import {PanelBankSectionsContainer} from './sections/PanelBankSectionsContainer';
import {type PanelBankProps} from './types';

interface PanelBankInnerProps {
  panelBankProps: PanelBankProps;
  setPanelSelection(args: any): any;
}

const PanelBankInnerComp = ({panelBankProps}: PanelBankInnerProps) => {
  const panelBankSectionsDomRef = useRef<HTMLDivElement>(null);

  const {panelBankWidth, setPanelBankWidth} = usePanelBankWidthContext();

  // Store panelBankWidth in a ref so onResize can read the latest value
  // without including it in the useCallback dependency array. panelBankWidth
  // changes on every resize, which would cause the callback to recreate each
  // time — defeating the purpose of memoization. The ref lets us do the
  // equality check without triggering a new callback identity.
  const panelBankWidthRef = useRef(panelBankWidth);
  panelBankWidthRef.current = panelBankWidth;

  const onResize = useCallback(
    contentRect => {
      const newWidth = contentRect.bounds
        ? Math.floor(contentRect.bounds.width) - PANEL_BANK_PADDING * 2
        : 0;
      if (newWidth !== panelBankWidthRef.current) {
        setPanelBankWidth(newWidth);
      }
    },
    [setPanelBankWidth]
  );

  return (
    <Measure bounds innerRef={panelBankSectionsDomRef} onResize={onResize}>
      {({measureRef}) => {
        return (
          <TailwindContents>
            <div ref={measureRef} className="flex grow flex-col">
              <PanelBankSectionsContainer
                isProjectReadOnly={!!panelBankProps.isProjectReadOnly}
              />
            </div>
          </TailwindContents>
        );
      }}
    </Measure>
  );
};

export const PanelBankInner = memo(
  PanelBankInnerComp,
  (prevProps, nextProps) => {
    return (
      prevProps.setPanelSelection === nextProps.setPanelSelection &&
      shallowEqual(prevProps.panelBankProps, nextProps.panelBankProps)
    );
  }
);
